import PropTypes from 'prop-types'
import React, { forwardRef, useEffect, useRef, useState } from 'react'

// The data-* props in the dataset shape the shared script reads from a control.
export function feedbackData(props) {
  return Object.fromEntries(
    Object.entries(props)
      .filter(([key]) => key.startsWith('data-'))
      .map(([key, value]) => [
        key.slice(5).replace(/-([a-z])/g, (_, c) => c.toUpperCase()),
        value,
      ])
  )
}

// Splits a composite's props: data-* attributes go to its controls, the rest to
// its root element.
export function splitData(props) {
  const data = {}
  const rest = {}
  Object.entries(props).forEach(([key, value]) => {
    if (key.startsWith('data-')) data[key] = value
    else rest[key] = value
  })
  return [data, rest]
}

export function isDisabled(props) {
  return (
    !!props.disabled ||
    props['aria-disabled'] === true ||
    props['aria-disabled'] === 'true'
  )
}

export function reportProblems(problems, control) {
  problems.forEach(problem => console.error(`MEButtons: ${problem}`, control))
}

// The native element keeps form association, submit behavior and link
// affordances.
export const Button = forwardRef(function Button(
  {
    href,
    children,
    icon,
    iconAfter,
    className = 'me-btn me-btn-secondary',
    onAction,
    onFailure,
    onSuccess,
    onCancel,
    onClick,
    abort,
    // Internal to BulkActionToolbar and Menu: the request survives responsive
    // reparenting, or a selection that unmounts the menu.
    requestOwner,
    ...props
  },
  forwardedRef
) {
  const localOwner = useRef({})
  const owner = requestOwner || localOwner.current
  const [state, setState] = useState(
    owner.state || { phase: 'idle', pending: false }
  )
  const action = useRef(null)
  const element = useRef(null)
  const failure = useRef(onFailure)
  failure.current = onFailure
  const success = useRef(onSuccess)
  success.current = onSuccess
  const cancelled = useRef(onCancel)
  cancelled.current = onCancel
  useEffect(() => {
    const { announce, createActionState, registerAction } = window.MEButtons
    owner.render = setState
    setState(owner.state || { phase: 'idle', pending: false })
    owner.callbacks = { failure, success, cancelled }
    if (!owner.action)
      owner.action = createActionState(
        state => {
          owner.state = state
          owner.render?.(state)
          if (state.cancelled) owner.callbacks.cancelled.current?.()
        },
        announce,
        error => owner.callbacks.failure.current?.(error),
        result => owner.callbacks.success.current?.(result)
      )
    action.current = owner.action
    const unregister = registerAction(element.current, action.current)
    return () => {
      unregister()
      if (owner.render === setState) owner.render = undefined
      if (!requestOwner) {
        owner.action.dispose()
        owner.action = undefined
      }
    }
  }, [])
  const { glyphClass, stateLabel, validateAction } = window.MEButtons
  const data = feedbackData(props)
  const abortable = data.abort !== undefined
  const blocked = isDisabled(props) || (state.pending && !abortable)
  const phase = state.phase
  const label =
    props.role === 'radio' ? children : stateLabel(data, phase, children)
  const Tag = href !== undefined ? 'a' : 'button'
  const guard = event => {
    const activates =
      event.type !== 'keydown' || ['Enter', ' '].includes(event.key)
    if (blocked && activates) {
      event.preventDefault()
      event.stopPropagation()
    }
  }
  return (
    <Tag
      {...props}
      ref={node => {
        element.current = node
        if (typeof forwardedRef === 'function') forwardedRef(node)
        else if (forwardedRef) forwardedRef.current = node
      }}
      data-me-react=""
      data-me-pending={state.pending ? '' : undefined}
      href={href}
      type={Tag === 'button' ? props.type || 'button' : undefined}
      className={`${className}${
        phase === 'success'
          ? ' is-success'
          : phase === 'failure'
          ? ' is-failure'
          : ''
      }`}
      aria-label={
        props['aria-label'] ||
        (onAction && typeof children === 'string' ? children : undefined)
      }
      aria-disabled={blocked && !props.disabled ? true : props['aria-disabled']}
      aria-busy={phase === 'busy' ? true : undefined}
      onClickCapture={guard}
      onKeyDownCapture={guard}
      onClick={event => {
        if (blocked || (action.current.pending && !abortable)) {
          event.preventDefault()
          return
        }
        if (action.current.pending && abortable) {
          event.preventDefault()
          action.current.cancel()
          return
        }
        if (onClick) onClick(event)
        if (!onAction || event.defaultPrevented) return
        event.preventDefault()
        // A segmented option keeps its label, like an icon button its glyph.
        const problems = validateAction(data, !!icon || props.role === 'radio')
        reportProblems(problems, element.current)
        if (problems.length) return
        let result
        try {
          result = onAction(event)
        } catch (error) {
          result = Promise.reject(error)
        }
        action.current.run(result, data, abort)
      }}
    >
      {icon && (
        <i
          aria-hidden="true"
          className={glyphClass(phase, `me-btn-glyph ${icon}`)}
        />
      )}
      {children !== undefined && (
        <span className="me-btn-label">
          {label !== children && typeof children !== 'string' ? (
            <>
              <span className="me-btn-sr-only">{children}</span>
              <span aria-hidden="true">{label}</span>
            </>
          ) : (
            label
          )}
        </span>
      )}
      {props.role === 'radio' && (
        <span className="me-segment-selected" aria-hidden="true">
          {children}
        </span>
      )}
      {iconAfter && <i aria-hidden="true" className={iconAfter} />}
    </Tag>
  )
})

Button.propTypes = {
  href: PropTypes.string,
  children: PropTypes.node,
  // The leading glyph, which the request states replace with the spinner,
  // check and cross.
  icon: PropTypes.string,
  // A trailing glyph the request states leave alone, such as a menu caret.
  iconAfter: PropTypes.string,
  className: PropTypes.string,
  // Returns the request's promise; the button runs its pending, busy and
  // result states.
  onAction: PropTypes.func,
  onFailure: PropTypes.func,
  onSuccess: PropTypes.func,
  onCancel: PropTypes.func,
  onClick: PropTypes.func,
  abort: PropTypes.func,
  requestOwner: PropTypes.object,
}
